import React from 'react'
import classNames from 'classnames'
import { useQuery } from 'react-query'
import { Button, Loader, Icon, HTMLText } from '@crossfit/ui'

import InnerContent from 'components/InnerContent'
import PageHeader from 'components/PageHeader'
import ImageGallery, {
  ImageGalleryImage,
  ImageGalleryThumbnail,
} from 'components/ImageGallery'
import * as programmingService from 'services/programming'
import NotAuthorized from 'components/NotAuthorized'
import usePageTitle from 'hooks/usePageTitle'
import style from './style.module.css'
import { trackEvent } from 'services/segment'
import { useAffiliate } from 'hooks/useAffiliate'
import { useProductAccess } from 'hooks/useProductAccess'

declare global {
  interface Window {
    dataLayer: unknown[]
  }
}

function usePlaybookContent() {
  const { canAccessPlaybook } = useAffiliate()
  const productAccess = useProductAccess()
  const query = useQuery(
    'playbook-content',
    programmingService.fetchPlaybookContent,
    {
      enabled: canAccessPlaybook,
    },
  )

  return {
    ...query,
    hasAccess:
      canAccessPlaybook ||
      productAccess.has(programmingService.SubscriptionPackageType.ATK_STARTER),
    content: query.data,
  }
}

function PlaybookPage() {
  const { selectedAffiliate } = useAffiliate()
  const { content, isLoading, isError, hasAccess } = usePlaybookContent()
  usePageTitle(
    `CrossFit Affiliate Toolkit${content ? ` | ${content.headlineText}` : ''}`,
  )

  if (isLoading) {
    return <Loader />
  }

  if (isError || !hasAccess) {
    return <NotAuthorized />
  }

  return (
    <>
      <PageHeader subheadlineText={content.subheadline}>
        {content.headlineText}
      </PageHeader>
      <InnerContent className={style.innerContent} as='main' variant='xlarge'>
        <ImageGallery className={style.imageGallery}>
          {({ activeIndex, setActiveIndex }) => (
            <>
              <ImageGalleryImage {...content.imageGallery[activeIndex].image} />
              {content.imageGallery.length > 1 &&
                content.imageGallery.map(({ image }, index) => (
                  <ImageGalleryThumbnail
                    key={`${image.alt}-${index}`}
                    index={index}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    {...image}
                  />
                ))}
            </>
          )}
        </ImageGallery>
        <div className={style.content}>
          <HTMLText className={style.htmlText}>{content.bodyText}</HTMLText>
          <table className={classNames('cf-table', style.table)}>
            <thead>
              <tr>
                <th>Language</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {content.pdfLinks.map(({ title, linkText, pdfUrl }) => (
                <tr key={title}>
                  <td>{title}</td>
                  <td>
                    <Button
                      variant='tertiary'
                      color='blue'
                      as='a'
                      href={pdfUrl}
                      target='_blank'
                      rel='noreferrer'
                      onClick={() => {
                        trackEvent({
                          event: 'View Playbook Clicked',
                          component: 'Playbook Page',
                          location: 'Affiliate Toolkit',
                          text: linkText,
                          linkUrl: pdfUrl,
                          tileName: title,
                          affiliateId: selectedAffiliate?.affiliateId,
                          userRole:
                            selectedAffiliate?.roles?.length > 0
                              ? selectedAffiliate?.roles[0]
                              : null,
                          groupId: selectedAffiliate?.groupId,
                        })
                      }}
                      rightIcon={<Icon name='arrow-right' />}
                    >
                      {linkText}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </InnerContent>
    </>
  )
}

export default PlaybookPage
